import { Directive, EventEmitter, Input, OnDestroy, OnInit, Output, Self } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl, ValidatorFn } from '@angular/forms';
import { GuidUtil } from '@shared/utils/guid-utils';
import { Subscription } from 'rxjs';

// Not to be used as an actual directive. This decorator is only used to remove
// Angular's warning for components extending a parent class.
@Directive({
  selector: '[spControlBase]',
})
export abstract class SpControlBaseDirective implements ControlValueAccessor, OnInit, OnDestroy {
  onChange: (_: any) => void;
  onTouched: () => void;
  formControl: FormControl = new FormControl();
  errorMessage = '';
  componentUniqueId: string;

  private subs: Subscription[] = [];

  // accessibility
  attributes: any = {};

  @Input() validators: ValidatorFn[];
  @Input() inputLabel: string;
  @Input() tooltipMessage: string;
  @Input() type: 'text' | 'number' | 'date' = 'text';
  @Input() mask = '';
  @Input() patterns: { [key: string]: { pattern: RegExp; symbol: string } };
  @Output() spBlur = new EventEmitter<FocusEvent>();

  constructor(@Self() public ngControl: NgControl) {
    this.componentUniqueId = GuidUtil.generateGuid();
    this.ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    this.formControl.setValidators(this.validators ?? []);
    this.ngControl.control?.setValidators(
      [this.ngControl.control.validator, () => this.formControl.errors].filter(x => x)
    );
    this.subs.push(
      this.formControl.valueChanges.subscribe(x => this.onChange(x)),
      this.formControl.statusChanges.subscribe(() => this.updateValidation()),
      this.ngControl.control.statusChanges.subscribe(() => this.updateValidation())
    );

    this.attributes = {
      required: this.checkIsRequired() ? '' : null,
    };
  }

  checkIsRequired(): boolean {
    const empty = new FormControl(null);
    const error = {
      ...this.ngControl.control.validator(empty),
      ...this.formControl.validator(empty),
    };

    return Object.keys(error).some(k => k === 'required');
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  writeValue(obj: any): void {
    this.formControl.setValue(obj, { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onTouch(e: FocusEvent): void {
    this.onTouched();
    this.spBlur.emit(e);
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.formControl.disable() : this.formControl.enable();
  }

  abstract updateValidation(): void;
}
